import { useQuery } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import { TimelineList } from '@procureai/ui-mui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible';
import { I18nNamespace } from '~/common/types';
import { BusinessLogItem } from '~/modules/BusinessLogWidget/components/BusinessLogItem/BusinessLogItem';

import { SAVING_HISTORY_QUERY } from './SavingHistorySection.query.gql';

const HISTORY_LIMIT = 100;

interface SavingHistorySectionProps {
  id: string | null;
}

export const SavingHistorySection = ({ id }: SavingHistorySectionProps) => {
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'Savings.history' });
  const [open, setOpen] = useState(true);
  const { data, loading } = useQuery(SAVING_HISTORY_QUERY, {
    variables: { entityId: id ?? '', limit: HISTORY_LIMIT },
    skip: !id,
  });

  if (!id) {
    return null;
  }

  const nodes = data?.telescope.logEntries.nodes ?? [];
  const totalCount = data?.telescope.logEntries.totalCount ?? nodes.length;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border-t pt-4">
      <CollapsibleTrigger className="mb-2 flex w-full items-center gap-2 text-sm font-medium text-foreground">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <span>{t('title')}</span>
        {totalCount ? <span className="text-muted-foreground">({totalCount})</span> : null}
        {loading ? <CircularProgress size={16} /> : null}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="max-h-72 overflow-y-auto pr-1">
          <TimelineList dense items={nodes} renderItem={BusinessLogItem} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
